"use client";

import React, { useRef, useState, useEffect } from "react";
import { motion, useSpring, useMotionValue, useTransform, useScroll } from "motion/react";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  CodeIcon,
  Layout01Icon,
  ShoppingCart01Icon,
  FilesIcon,
  SmartPhone01Icon,
  HeartPulseIcon,
  CompassIcon,
  AnalyticsUpIcon,
  Database01Icon,
} from "@hugeicons/core-free-icons";
import { Chip } from "@heroui/react";

const ServiceArray = [
  {
    id: "website-development",
    name: "Website Development",
    description:
      "Building custom websites from scratch or using popular frameworks/platforms.",
    category: "Development",
    icon: (
      <HugeiconsIcon
        icon={CodeIcon}
        className="h-12 w-12"
        size={48}
      />
    ),
    tags: ["Custom UI", "Next.js/React", "Node.js", "API Dev"],
  },
  {
    id: "ui-ux-design",
    name: "UI/UX Design",
    description:
      "Creating intuitive, user-friendly, and visually appealing interfaces and experiences.",
    category: "Design",
    icon: (
      <HugeiconsIcon
        icon={Layout01Icon}
        className="h-12 w-12"
        size={48}
      />
    ),
    tags: ["UI/UX Design", "Figma", "User Flow", "Prototyping"],
  },
  {
    id: "e-commerce-solutions",
    name: "E-commerce Solutions",
    description:
      "Developing online stores with shopping carts, payment gateways, and product management.",
    category: "Development",
    icon: (
      <HugeiconsIcon
        icon={ShoppingCart01Icon}
        className="h-12 w-12"
        size={48}
      />
    ),
    tags: ["Shopify", "Stripe", "Checkout", "WooCommerce"],
  },
  {
    id: "cms-development",
    name: "CMS Development & Integration",
    description:
      "Implementing and customizing Content Management Systems for easy content updates.",
    category: "Development",
    icon: (
      <HugeiconsIcon
        icon={FilesIcon}
        className="h-12 w-12"
        size={48}
      />
    ),
    tags: ["WordPress", "Headless CMS", "Sanity", "Migration"],
  },
  {
    id: "mobile-app-development",
    name: "Mobile App Development",
    description:
      "Building native or cross-platform mobile applications for iOS and Android.",
    category: "Development",
    icon: (
      <HugeiconsIcon
        icon={SmartPhone01Icon}
        className="h-12 w-12"
        size={48}
      />
    ),
    tags: ["React Native", "Flutter", "iOS/Android", "App Stores"],
  },
  {
    id: "website-maintenance-support",
    name: "Website Maintenance & Support",
    description:
      "Providing ongoing updates, security monitoring, backups, and technical support.",
    category: "Ongoing Services",
    icon: (
      <HugeiconsIcon
        icon={HeartPulseIcon}
        className="h-12 w-12"
        size={48}
      />
    ),
    tags: ["Security Audits", "Updates", "Backups", "Troubleshooting"],
  },
  {
    id: "web-strategy-consulting",
    name: "Web Strategy & Consulting",
    description:
      "Offering expert advice on project planning, technology stack selection, and digital strategy.",
    category: "Strategy",
    icon: (
      <HugeiconsIcon
        icon={CompassIcon}
        className="h-12 w-12"
        size={48}
      />
    ),
    tags: ["Consulting", "Tech Auditing", "Specs Planning", "Discovery"],
  },
  {
    id: "seo-optimization",
    name: "Search Engine Optimization (SEO)",
    description:
      "Improving website visibility and ranking in search engine results.",
    category: "Related Services",
    icon: (
      <HugeiconsIcon
        icon={AnalyticsUpIcon}
        className="h-12 w-12"
        size={48}
      />
    ),
    tags: ["Keywords", "On-Page SEO", "Technical Audit", "Rank Tracker"],
  },
  {
    id: "web-hosting-domains",
    name: "Web Hosting & Domain Management",
    description:
      "Setting up and managing reliable web hosting environments and domain names.",
    category: "Related Services",
    icon: (
      <HugeiconsIcon
        icon={Database01Icon}
        className="h-12 w-12"
        size={48}
      />
    ),
    tags: ["AWS/Vercel", "DNS Config", "CDN Setup", "CI/CD Setup"],
  },
];

export default function Services() {
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isDark, setIsDark] = useState(false);
  const [xRange, setXRange] = useState(0);

  // Sync theme changes
  useEffect(() => {
    const checkDark = () => {
      setIsDark(document.documentElement.classList.contains("dark"));
    };
    checkDark();
    const observer = new MutationObserver(checkDark);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => observer.disconnect();
  }, []);

  // Calculate horizontal translation bounds dynamically
  useEffect(() => {
    const updateRange = () => {
      if (scrollRef.current) {
        const scrollWidth = scrollRef.current.scrollWidth;
        const clientWidth = window.innerWidth;
        const maxScroll = -(scrollWidth - clientWidth);
        setXRange(maxScroll < 0 ? maxScroll : 0);
      }
    };
    
    // Initial measurement
    updateRange();
    
    // Extra checks to handle layout shifts or delayed rendering of child components
    const timer = setTimeout(updateRange, 100);
    const observer = new ResizeObserver(updateRange);
    if (scrollRef.current) {
      observer.observe(scrollRef.current);
    }

    window.addEventListener("resize", updateRange);
    return () => {
      window.removeEventListener("resize", updateRange);
      observer.disconnect();
      clearTimeout(timer);
    };
  }, []);

  // Track mouse movements for background mesh parallax translation
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!containerRef.current) return;
    const { clientX, clientY } = e;
    const { innerWidth, innerHeight } = window;
    mouseX.set(clientX / innerWidth - 0.5);
    mouseY.set(clientY / innerHeight - 0.5);
  };

  const handleMouseLeave = () => {
    mouseX.set(0);
    mouseY.set(0);
  };

  const springX = useSpring(mouseX, { stiffness: 80, damping: 20 });
  const springY = useSpring(mouseY, { stiffness: 80, damping: 20 });

  const bgTranslateX = useTransform(springX, [-0.5, 0.5], [-35, 35]);
  const bgTranslateY = useTransform(springY, [-0.5, 0.5], [-35, 35]);

  // Scroll tracking and mapping
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"],
  });

  const x = useTransform(scrollYProgress, [0, 1], [0, xRange]);

  // Mesh Colors based on light/dark mode
  const meshColors1 = isDark
    ? [
        "rgba(88, 28, 135, 0.15)", // Purple-900
        "rgba(30, 58, 138, 0.15)", // Indigo-900
        "rgba(76, 29, 149, 0.15)", // Violet-950
        "rgba(88, 28, 135, 0.15)",
      ]
    : [
        "rgba(139, 92, 246, 0.08)", // Violet-500
        "rgba(59, 130, 246, 0.08)", // Blue-500
        "rgba(168, 85, 247, 0.08)", // Purple-500
        "rgba(139, 92, 246, 0.08)",
      ];

  return (
    <div
      ref={containerRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      className="w-full md:h-[300vh] bg-neutral-50 dark:bg-black transition-colors duration-500 relative"
    >
      <h2 className="sr-only">Our Services</h2>
      {/* Background Interactive Mesh and Grid Backdrop */}
      <div className="absolute inset-0 -z-10 overflow-hidden pointer-events-none">
        <motion.div
          style={{ x: bgTranslateX, y: bgTranslateY }}
          className="absolute -top-[10%] left-[20%] h-[500px] w-[500px] rounded-full blur-[130px]"
          animate={{
            scale: [1, 1.08, 0.92, 1],
            backgroundColor: meshColors1,
          }}
          transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
        />
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808006_1px,transparent_1px),linear-gradient(to_bottom,#80808006_1px,transparent_1px)] bg-[size:32px_32px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_50%,#000_85%,transparent_100%)]" />
      </div>

      <div className="md:sticky md:top-0 md:h-screen w-full overflow-hidden flex flex-col justify-center">
        {/* Large background watermarked title */}
        <motion.div
          initial={{ opacity: 0, y: 80 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ type: "spring", duration: 1.2, delay: 0.2 }}
          className="absolute lg:-top-24 md:top-10 -top-8 min-w-full select-none pointer-events-none"
        >
          <div className="text-center text-sm/normal md:text-xs/tight xl:text-[26rem] lg:text-[18rem] md:text-[13rem] sm:text-[10rem] text-[4rem] bg-clip-text bg-gradient-to-b from-blue-600/30 dark:from-blue-400/20 to-80% dark:to-80% to-transparent text-bg-clip text-transparent font-bold font-recoleta tracking-wide">
            Service
          </div>
        </motion.div>

        {/* Desktop Carousel View */}
        <div className="max-w-full py-8 hidden md:flex overflow-hidden">
          <motion.div
            ref={scrollRef}
            style={{ x }}
            className="w-max flex flex-nowrap gap-6 select-none px-64"
          >
            {ServiceArray.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-20px" }}
                transition={{ type: "spring", stiffness: 150, damping: 18 }}
                whileHover={{ y: -12 }}
                className="min-w-96 group bg-gradient-to-b from-violet-200/10 dark:from-violet-950/5 group-hover:to-violet-750/30 dark:group-hover:to-rose-950/20 from-70% to-violet-400/20 dark:to-violet-850/10 border-t-0 border border-blue-300/15 dark:border-blue-900/15 shadow-[0_8px_32px_0_rgba(31,38,135,0.02)] backdrop-blur-xl rounded-4xl p-2.5 transition-all duration-500 cursor-pointer"
              >
                <div className="h-full">
                  {/* Radial glow background wrapper for the icon */}
                  <div className="w-full h-60 flex justify-center items-center bg-radial from-blue-900/15 dark:from-blue-950/30 to-60% to-transparent rounded-2xl">
                    <div className="flex justify-center items-center rounded-full">
                      <motion.span
                        className="text-neutral-800 dark:text-neutral-100 group-hover:text-blue-500 dark:group-hover:text-blue-400 transition-colors duration-300"
                        whileHover={{ scale: 1.12, rotate: 3 }}
                        transition={{
                          type: "spring",
                          stiffness: 300,
                          damping: 12,
                        }}
                      >
                        {service.icon}
                      </motion.span>
                    </div>
                  </div>
                  {/* Text area */}
                  <div className="p-6 relative">
                    <div className="group-hover:translate-x-3 transition-transform duration-300 ease-out">
                      <h3 className="text-2xl font-bold text-neutral-900 dark:text-white mb-2.5 font-recoleta tracking-tight group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-300">
                        {service.name}
                      </h3>
                      <p className="text-neutral-500 dark:text-neutral-400/80 text-base font-manjari leading-relaxed transition-colors duration-300">
                        {service.description}
                      </p>
                      {/* Tags list */}
                      <div className="flex flex-wrap gap-1.5 mt-4">
                        {service.tags.map((tag, idx) => (
                          <Chip
                            key={idx}
                            variant="soft"
                            className="bg-neutral-100 dark:bg-neutral-850/50 text-[10px] text-neutral-600 dark:text-neutral-450 border-none font-outfit"
                          >
                            <Chip.Label>{tag}</Chip.Label>
                          </Chip>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>

        {/* Mobile Vertical View */}
        <div className="max-w-[99vw] min-h-screen py-24 px-3 md:hidden flex flex-col z-10 justify-center">
          <div className="mt-8 flex flex-col gap-4">
            {ServiceArray.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.05 }}
                whileHover={{ y: -6 }}
                className="min-w-[94vw] group bg-gradient-to-b from-violet-200/10 dark:from-violet-950/5 group-hover:to-violet-750/30 dark:group-hover:to-rose-950/20 from-70% to-violet-400/20 dark:to-violet-850/10 border-t-0 border border-blue-300/15 dark:border-blue-900/15 shadow-[0_8px_32px_0_rgba(31,38,135,0.02)] backdrop-blur-xl rounded-2xl p-2.5 transition-all duration-500 cursor-pointer"
              >
                <div className="h-full">
                  <div className="w-full h-60 flex justify-center items-center bg-radial from-blue-900/15 dark:from-blue-950/30 to-60% to-transparent rounded-2xl">
                    <div className="flex justify-center items-center rounded-full">
                      <span className="text-neutral-800 dark:text-neutral-100 group-hover:text-blue-500 dark:group-hover:text-blue-400 transition-colors duration-300">
                        {service.icon}
                      </span>
                    </div>
                  </div>
                  {/* Text area */}
                  <div className="p-6 relative">
                    <div className="group-hover:translate-x-3 transition-transform duration-300 ease-out">
                      <h3 className="text-2xl font-bold text-neutral-900 dark:text-white mb-2.5 font-recoleta tracking-tight">
                        {service.name}
                      </h3>
                      <p className="text-neutral-500 dark:text-neutral-400/80 text-base font-manjari leading-relaxed">
                        {service.description}
                      </p>
                      {/* Tags list */}
                      <div className="flex flex-wrap gap-1.5 mt-4">
                        {service.tags.map((tag, idx) => (
                          <Chip
                            key={idx}
                            variant="soft"
                            className="bg-neutral-100 dark:bg-neutral-850/50 text-[10px] text-neutral-600 dark:text-neutral-450 border-none font-outfit"
                          >
                            <Chip.Label>{tag}</Chip.Label>
                          </Chip>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

